/**
 * FindingsView — consolidated DQ findings tab in Data Profiler.
 *
 * Merges two categories of findings into a single, sortable, filterable
 * list with a backlog-export action:
 *
 *  - **Statistical** findings from profiling (null %, outliers,
 *    cardinality, singleton values, etc.) — emitted by dq_engine.
 *  - **Business-rule** findings from active library rules executed during
 *    the profile pass (`_run_business_rules` in data_profiling_routes.py).
 *
 * The two feeds are distinguishable by `category==='business_rule'`.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertCircle,
  AlertTriangle,
  BarChart3,
  Download,
  EyeOff,
  FileCode,
  FileSpreadsheet,
  Filter,
  ListChecks,
  RotateCcw,
  ShieldCheck,
  Wrench,
} from "lucide-react";
import { rulesApi } from "../../api/rules";
import { useAuth } from "../../auth";
import {
  downloadViolationBundle,
  downloadViolationExtract,
  downloadViolationQuery,
  listViolationQueries,
  type ViolationListing,
  exportFindingsExcel,
  sendFindingsToBacklog,
} from "../../api/profiler-api";
import { api } from "../../api";
import { useNavigate } from "react-router-dom";

/** The finding category the opt-in "Deep relationship discovery" emits: suggestions from values only, listed apart from
 *  the findings and excluded from the counts and the quality score. */
export const POSSIBLE_RELATIONSHIP_CATEGORY = "possible_relationship";

export interface FindingIssue {
  id: string;
  rule_id?: string;
  table: string;
  column?: string;
  category: string;
  /** The detector (business insight) that produced the finding, e.g. ``sentinel_value``. */
  subcategory?: string;
  /** How to select the finding's rows (value-free); ``kind`` tells two statistical findings on one column apart. */
  row_set?: { kind?: string } | null;
  /** The finding's handle for its violation query / extract (stable within the session). */
  violation_id?: string;
  severity: string;
  description: string;
  impact?: string;
  evidence?: string;
  suggested_fix?: string;
  affected_columns?: string[];
  /** Up to 25 sample violating rows captured at profile time. */
  sample_failures?: Array<Record<string, unknown>>;
  /** Column order for rendering sample_failures as a table. */
  sample_failure_columns?: string[];
  /** Provenance of a business-rule finding (DQ rule governance): the knowledge pack and version it came from, the
   *  rule's origin, the fingerprint of the rule version that ran, and how that version was approved. */
  pack_name?: string | null;
  pack_version?: string | null;
  origin?: string;
  rule_fingerprint?: string;
  rule_approval_kind?: string | null;
  rule_derived_from?: string;
}

export interface FindingRule {
  id: string;
  rule_text?: string;
  rule_type?: string;
  table?: string;
  state?: string;
  status?: string;
  failing_rows?: number;
  total_rows?: number;
}

export interface FindingsViewProps {
  issues: FindingIssue[];
  rules: FindingRule[];
  projectId: string;
  /** Profiler session id — needed to export findings to Excel / backlog. */
  sessionId?: string;
  /**
   * Canonical source name for the active dataset, as the backend reports it on
   * the profile results (``results.source_name``: ``"postgresql://nslds"`` for a
   * database, the file name for a file). Do not derive it in the UI. Used to
   * scope ignored findings — same (project, source) pair drops the same finding
   * on subsequent runs. ``sessionId`` is also sent so the backend resolves the
   * key itself (and matches ignores saved under older keys).
   */
  sourceName?: string;
  /**
   * Days 9-10: click-through from a finding to a pre-filled rule draft.
   * If provided, each finding gets a "Promote to rule" button that hands
   * the finding to the parent; parent typically switches to the Rules tab
   * and seeds RuleCreateForm with a matching template.
   */
  onPromoteToRule?: (finding: FindingIssue) => void;
}

const SEVERITY_ORDER: Record<string, number> = {
  critical: 0,
  high: 1,
  poor: 2,
  medium: 3,
  warning: 3,
  moderate: 3,
  low: 4,
  good: 5,
  excellent: 6,
};

function severityPill(sev: string) {
  const s = sev.toLowerCase();
  if (s === "critical" || s === "high") {
    return "bg-red-400/15 text-red-200 border border-red-400/30";
  }
  if (s === "poor") {
    return "bg-orange-400/15 text-orange-200 border border-orange-400/30";
  }
  if (s === "medium" || s === "warning" || s === "moderate") {
    return "bg-amber-400/15 text-amber-200 border border-amber-400/30";
  }
  if (s === "low" || s === "good") {
    return "bg-sky-400/15 text-sky-200 border border-sky-400/30";
  }
  return "bg-white/5 text-white/65 border border-white/10";
}

export function FindingsView({
  issues,
  rules,
  projectId,
  sessionId,
  sourceName = "",
  onPromoteToRule,
}: FindingsViewProps) {
  // Track ignored finding signatures locally so the user gets immediate
  // feedback when they click Ignore — we don't wait for a refetch round-trip.
  // The backend filter pass during the next profile run is the source of
  // truth for persistence; this set is just an optimistic UI overlay.
  const [ignoredSigs, setIgnoredSigs] = useState<Set<string>>(new Set());
  // Violation queries (tier 1): which findings have one, keyed by violation_id. Extract (tier 2) needs its own grant.
  const [violations, setViolations] = useState<Map<string, ViolationListing>>(
    new Map(),
  );
  const { hasPermission } = useAuth();
  const canExtract = hasPermission("profiler:export_violations");
  // Per-finding selection (fingerprints) for Export → Excel / Backlog.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // Server-side ignored records (with id, table, column, reason) so we can
  // render the "Show ignored" panel and offer Restore. Loaded on mount and
  // synced as the user clicks Ignore / Restore.
  type IgnoredRecord = {
    id: string;
    project_id: string;
    source_name: string;
    signature: string;
    table_name: string;
    column_name: string;
    category: string;
    rule_id: string | null;
    reason: string;
    created_at: string;
  };
  const [ignoredRecords, setIgnoredRecords] = useState<IgnoredRecord[]>([]);
  const [showIgnored, setShowIgnored] = useState(false);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    listViolationQueries(sessionId)
      .then((res) => {
        if (!cancelled)
          setViolations(new Map(res.findings.map((v) => [v.violation_id, v])));
      })
      .catch((err) => console.warn("Violation queries unavailable:", err));
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  // Load any previously ignored findings for this (project, source) pair so
  // (a) they don't reappear in the visible list if the backend filter pass
  // hasn't run yet, and (b) the "Show ignored (N)" panel can list them.
  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await rulesApi.ignoredFindings.list(
          projectId,
          sourceName,
          sessionId,
        );
        const items = (res.items ?? []) as IgnoredRecord[];
        if (!cancelled) {
          setIgnoredRecords(items);
          setIgnoredSigs(new Set(items.map((i) => i.signature)));
        }
      } catch (err) {
        // Non-fatal — Findings still renders, just without the previously
        // ignored overlay. Backend filter still suppresses on next run.

        console.warn("Failed to load ignored findings:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId, sourceName, sessionId]);

  // Must match the backend's ``signature_for_finding`` (ignored_findings.py): what the next run's filter drops.
  function legacyFingerprint(f: FindingIssue): string {
    const cat = (f.category || "").toLowerCase();
    const tbl = f.table || "";
    const col = f.column || (f.affected_columns?.[0] ?? "*") || "*";
    return `${tbl}|${col}|${cat}`;
  }

  function fingerprint(f: FindingIssue): string {
    const cat = (f.category || "").toLowerCase();
    const tbl = f.table || "";
    if (cat === "business_rule") {
      return `rule:${f.rule_id ?? f.id}|${tbl}`;
    }
    // The detector (or statistical row-set kind) is part of the key: two findings on one column (a sentinel and a
    // format variation; nulls and empty strings) used to share it, so ignoring or selecting one took both.
    const disc = f.subcategory || f.row_set?.kind || "";
    return disc ? `${legacyFingerprint(f)}|${disc}` : legacyFingerprint(f);
  }

  // An ignore saved before the detector was part of the key still hides what it hid then.
  function isIgnored(f: FindingIssue, sigs: Set<string>): boolean {
    return sigs.has(fingerprint(f)) || sigs.has(legacyFingerprint(f));
  }

  const handleIgnore = async (f: FindingIssue) => {
    if (!projectId) return;
    const sig = fingerprint(f);
    setIgnoredSigs((s) => new Set(s).add(sig)); // optimistic
    try {
      const created = await rulesApi.ignoredFindings.create({
        project_id: projectId,
        source_name: sourceName,
        session_id: sessionId,
        finding: {
          id: f.id,
          rule_id: f.rule_id,
          category: f.category,
          subcategory: f.subcategory,
          row_set: f.row_set ? { kind: f.row_set.kind } : undefined,
          table: f.table,
          column: f.column,
          affected_columns: f.affected_columns,
        },
        reason: "Dismissed from Findings tab",
      });
      // Track the server-side record so the "Show ignored" panel and
      // restore action have something to work with.
      setIgnoredRecords((rs) => {
        if (rs.some((r) => r.id === created.id)) return rs;
        return [created as IgnoredRecord, ...rs];
      });
    } catch (err) {
      // Roll back the optimistic hide so the user can retry
      setIgnoredSigs((s) => {
        const next = new Set(s);
        next.delete(sig);
        return next;
      });

      console.warn("Failed to ignore finding:", err);
    }
  };

  const handleRestore = async (rec: IgnoredRecord) => {
    // Optimistically un-hide so the matching finding reappears immediately
    // on the current run's list. Roll back if the API call fails.
    const sigToDrop = rec.signature;
    setIgnoredSigs((s) => {
      const next = new Set(s);
      next.delete(sigToDrop);
      return next;
    });
    setIgnoredRecords((rs) => rs.filter((r) => r.id !== rec.id));
    try {
      await rulesApi.ignoredFindings.remove(rec.id);
    } catch (err) {
      setIgnoredSigs((s) => new Set(s).add(sigToDrop));
      setIgnoredRecords((rs) => [rec, ...rs]);

      console.warn("Failed to restore ignored finding:", err);
    }
  };

  const navigate = useNavigate();
  const [categoryFilter, setCategoryFilter] = useState<
    | "all"
    | "statistical"
    | "business_context"
    | "business_rule"
    | "possible_relationship"
  >("all");
  const [severityFilter, setSeverityFilter] = useState<string>("");
  const [tableFilter, setTableFilter] = useState<string>("");
  const [exporting, setExporting] = useState(false);
  const [exportErr, setExportErr] = useState<string | null>(null);
  // Elapsed seconds since a "Send to Backlog" analysis started, or null when
  // none is in flight — drives a live ticker on the button so a legitimately
  // slow (but working) ~60-180s LLM pass doesn't read as a hung UI.
  const [backlogElapsedSec, setBacklogElapsedSec] = useState<number | null>(
    null,
  );
  const backlogTickerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const statistical = useMemo(
    () =>
      issues.filter(
        (i) =>
          ![
            "business_rule",
            "business_insight",
            "business_insight_llm",
            POSSIBLE_RELATIONSHIP_CATEGORY,
          ].includes((i.category || "").toLowerCase()),
      ),
    [issues],
  );
  const possibleRelationships = useMemo(
    () =>
      issues.filter(
        (i) =>
          (i.category || "").toLowerCase() === POSSIBLE_RELATIONSHIP_CATEGORY,
      ),
    [issues],
  );
  const businessContext = useMemo(
    () =>
      issues.filter((i) =>
        ["business_insight", "business_insight_llm"].includes(
          (i.category || "").toLowerCase(),
        ),
      ),
    [issues],
  );
  const businessRule = useMemo(
    () =>
      issues.filter(
        (i) => (i.category || "").toLowerCase() === "business_rule",
      ),
    [issues],
  );

  const tables = useMemo(() => {
    const s = new Set<string>();
    issues.forEach((i) => i.table && s.add(i.table));
    return Array.from(s).sort();
  }, [issues]);
  // The "Tables with issues" count excludes suggestions: they are not findings.
  const tablesWithIssues = useMemo(() => {
    const s = new Set<string>();
    issues.forEach(
      (i) =>
        i.table &&
        (i.category || "").toLowerCase() !== POSSIBLE_RELATIONSHIP_CATEGORY &&
        s.add(i.table),
    );
    return s;
  }, [issues]);

  const filtered = useMemo(() => {
    let list =
      categoryFilter === "statistical"
        ? statistical
        : categoryFilter === "business_context"
          ? businessContext
          : categoryFilter === "business_rule"
            ? businessRule
            : categoryFilter === "possible_relationship"
              ? possibleRelationships
              : issues;
    if (severityFilter) {
      list = list.filter(
        (i) => (i.severity || "").toLowerCase() === severityFilter,
      );
    }
    if (tableFilter) {
      list = list.filter((i) => i.table === tableFilter);
    }
    // Drop optimistically-ignored findings so the UI matches what the
    // next profile run will produce (filter pass on backend).
    if (ignoredSigs.size > 0) {
      list = list.filter((i) => !isIgnored(i, ignoredSigs));
    }
    return [...list].sort(
      (a, b) =>
        (SEVERITY_ORDER[a.severity?.toLowerCase() ?? ""] ?? 9) -
        (SEVERITY_ORDER[b.severity?.toLowerCase() ?? ""] ?? 9),
    );
    // `fingerprint` is a stable closure over no state; safe to omit from deps.
  }, [
    issues,
    statistical,
    businessRule,
    possibleRelationships,
    categoryFilter,
    severityFilter,
    tableFilter,
    ignoredSigs,
  ]);

  // Selected findings → their rule_ids (dedup). Empty selection → undefined,
  // and the backend defaults to the actionable business-rule findings.
  const selectedRuleIds = useMemo(() => {
    if (selected.size === 0) return undefined;
    const ids = new Set<string>();
    for (const i of filtered) {
      if (selected.has(fingerprint(i))) {
        const rid = i.rule_id || i.id;
        if (rid) ids.add(String(rid));
      }
    }
    return ids.size ? [...ids] : undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, filtered]);

  const exportExcel = async () => {
    if (!sessionId) {
      setExportErr("Export requires an active profiling session.");
      return;
    }
    try {
      setExporting(true);
      setExportErr(null);
      await exportFindingsExcel(sessionId, selectedRuleIds);
    } catch (err) {
      setExportErr(err instanceof Error ? err.message : String(err));
    } finally {
      setExporting(false);
    }
  };

  const exportViolationQueries = async () => {
    if (!sessionId) {
      setExportErr("Export requires an active profiling session.");
      return;
    }
    const ids = filtered
      .filter((i) => selected.size === 0 || selected.has(fingerprint(i)))
      .map((i) => i.violation_id)
      .filter((v): v is string => !!v);
    try {
      setExporting(true);
      setExportErr(null);
      await downloadViolationBundle(sessionId, selected.size ? ids : null);
    } catch (err) {
      setExportErr(err instanceof Error ? err.message : String(err));
    } finally {
      setExporting(false);
    }
  };

  const sendToBacklog = async () => {
    if (!projectId || !sessionId) {
      setExportErr(
        "Backlog export requires a project + active session. Open the profiler via a project.",
      );
      return;
    }
    try {
      setExporting(true);
      setExportErr(null);
      setBacklogElapsedSec(0);
      backlogTickerRef.current = setInterval(() => {
        setBacklogElapsedSec((s) => (s === null ? null : s + 1));
      }, 1000);

      const res = await sendFindingsToBacklog(
        projectId,
        sessionId,
        selectedRuleIds,
      );
      // The analyze LLM pass runs async in the worker (a synchronous call
      // here could 504 on a slow model response) — poll the job the same
      // way BacklogArchitectView's document_analyze upload path does.
      // The worker task's own hard limit is 300s
      // (ztd_app/worker/tasks/backlog_documents.py analyze_findings,
      // task_time_limit=300). 210 * 2s = 420s gives 120s of headroom past
      // that for queueing delay + poll-request latency, so this can no
      // longer time out client-side while the job is still legitimately
      // running server-side (a prior 90*2s=180s window did exactly that).
      let backlogSessionId: string | null = null;
      for (let i = 0; i < 210; i++) {
        await new Promise((r) => setTimeout(r, 2000));
        const job = await api.backlogGetJob(res.job_id);
        if (job.status === "completed") {
          backlogSessionId = job.session_id ?? null;
          break;
        }
        if (job.status === "failed") {
          throw new Error(job.error_detail || "Backlog analysis failed");
        }
      }
      if (!backlogSessionId) {
        throw new Error("Backlog analysis timed out — please try again.");
      }
      navigate(
        `/projects/${projectId}/agents/backlog-architect?session=${backlogSessionId}`,
      );
    } catch (err) {
      setExportErr(err instanceof Error ? err.message : String(err));
    } finally {
      if (backlogTickerRef.current) {
        clearInterval(backlogTickerRef.current);
        backlogTickerRef.current = null;
      }
      setBacklogElapsedSec(null);
      setExporting(false);
    }
  };

  // Ticker must not outlive the component (e.g. user navigates away
  // mid-analysis) — the async poll loop's own state updates are already
  // harmless no-ops after unmount, but the interval itself keeps firing
  // and leaking otherwise.
  useEffect(() => {
    return () => {
      if (backlogTickerRef.current) clearInterval(backlogTickerRef.current);
    };
  }, []);

  return (
    <div className="space-y-5">
      {/* Summary strip */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <SummaryCard
          icon={<AlertCircle className="w-4 h-4 text-amber-300" />}
          label="Statistical findings"
          value={statistical.length}
          tone="amber"
        />
        <SummaryCard
          icon={<BarChart3 className="w-4 h-4 text-violet-300" />}
          label="Business-Context findings"
          value={businessContext.length}
          tone="violet"
        />
        <SummaryCard
          icon={<ShieldCheck className="w-4 h-4 text-red-300" />}
          label="Business-rule violations"
          value={businessRule.length}
          tone="red"
        />
        <SummaryCard
          icon={<AlertTriangle className="w-4 h-4 text-sky-300" />}
          label="Tables with issues"
          value={tablesWithIssues.size}
          tone="sky"
        />
      </div>

      {/* Filters + export */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex items-center rounded-md border border-white/10 overflow-hidden text-xs">
          {(
            [
              "all",
              "statistical",
              "business_context",
              "business_rule",
              ...(possibleRelationships.length > 0
                ? (["possible_relationship"] as const)
                : []),
            ] as const
          ).map((k) => (
            <button
              key={k}
              onClick={() => setCategoryFilter(k)}
              className={`px-3 py-1.5 transition-colors ${
                categoryFilter === k
                  ? "bg-[var(--primary)]/20 text-[var(--primary)]"
                  : "text-white/60 hover:text-white/90 hover:bg-white/5"
              }`}
            >
              {k === "all"
                ? "All"
                : k === "statistical"
                  ? "Statistical"
                  : k === "business_context"
                    ? "Business-Context"
                    : k === "possible_relationship"
                      ? `Possible relationships (${possibleRelationships.length})`
                      : "Business-rule"}
            </button>
          ))}
        </div>

        <Filter className="w-3.5 h-3.5 text-white/40 ml-1" />
        <select
          value={severityFilter}
          onChange={(e) => setSeverityFilter(e.target.value)}
          className="text-xs px-2 py-1.5 rounded-md bg-white/5 text-white/80 outline-none focus:ring-1 focus:ring-[var(--primary)]"
        >
          <option value="">Any severity</option>
          {["critical", "high", "medium", "low"].map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>

        <select
          value={tableFilter}
          onChange={(e) => setTableFilter(e.target.value)}
          className="text-xs px-2 py-1.5 rounded-md bg-white/5 text-white/80 outline-none focus:ring-1 focus:ring-[var(--primary)]"
        >
          <option value="">Any table</option>
          {tables.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>

        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-white/55">
            {filtered.length} / {issues.length}
          </span>
          {/* Show ignored toggle — surfaces previously dismissed findings
              with a Restore action. Hidden when there are no ignored
              entries to keep the bar uncluttered. */}
          {ignoredRecords.length > 0 && (
            <button
              onClick={() => setShowIgnored((v) => !v)}
              className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border transition-colors ${
                showIgnored
                  ? "bg-white/10 border-white/20 text-white/90"
                  : "border-white/10 text-white/60 hover:bg-white/5 hover:text-white/80"
              }`}
              title={
                showIgnored
                  ? "Hide previously ignored findings"
                  : "Show previously ignored findings + restore them"
              }
            >
              <EyeOff className="w-3.5 h-3.5" />
              {showIgnored ? "Hide" : "Show"} ignored ({ignoredRecords.length})
            </button>
          )}
          <span className="text-[11px] text-white/45 self-center">
            {selected.size > 0
              ? `${selected.size} selected`
              : "all business-rule findings"}
          </span>
          <button
            onClick={exportExcel}
            disabled={exporting || !sessionId}
            title="Download selected findings as an Excel workbook (for Jira / ServiceNow / Azure DevOps import)"
            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-white/5 hover:bg-white/10 text-white/80 border border-white/10 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Download className="w-3.5 h-3.5" />
            {exporting ? "Exporting…" : "Export Excel"}
          </button>
          <button
            onClick={exportViolationQueries}
            disabled={exporting || !sessionId || violations.size === 0}
            title="Download the read-only SQL that returns each finding's failing rows, in your database's own dialect, with a manifest. Logic only: the data never leaves your environment."
            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-white/5 hover:bg-white/10 text-white/80 border border-white/10 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <FileCode className="w-3.5 h-3.5" />
            Violation queries
          </button>
          <button
            onClick={sendToBacklog}
            disabled={exporting || !sessionId}
            title={
              backlogElapsedSec !== null
                ? "LLM analysis of the selected findings is running — this typically takes 60-180s"
                : "Create a Scrum Planner backlog from the selected findings"
            }
            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-200 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {backlogElapsedSec !== null ? (
              <>
                <span className="w-3.5 h-3.5 rounded-full border-2 border-emerald-200/40 border-t-emerald-200 animate-spin" />
                Analyzing… {backlogElapsedSec}s
              </>
            ) : (
              <>
                <ListChecks className="w-3.5 h-3.5" />
                {exporting ? "Exporting…" : "Send to Backlog"}
              </>
            )}
          </button>
        </div>
      </div>

      {/* Ignored findings panel — collapsed by default, opens via the
          "Show ignored (N)" toggle above. Restore returns the finding to
          the active list and removes the suppression server-side so the
          next profile run will surface it again. */}
      {showIgnored && ignoredRecords.length > 0 && (
        <div className="glass-surface rounded-xl overflow-hidden border border-white/10">
          <div className="flex items-center justify-between px-3.5 py-2 border-b border-white/10 bg-white/[0.02]">
            <div className="flex items-center gap-2 text-xs text-white/60">
              <EyeOff className="w-3.5 h-3.5" />
              <span>
                {ignoredRecords.length} ignored finding
                {ignoredRecords.length === 1 ? "" : "s"} for this dataset
              </span>
            </div>
            <span className="text-[11px] text-white/40">
              These will not appear in subsequent profile runs until restored.
            </span>
          </div>
          <ul className="divide-y divide-white/5">
            {ignoredRecords.map((rec) => (
              <li key={rec.id} className="flex items-start gap-3 px-3.5 py-2.5">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 text-[11px]">
                    <span className="text-[11px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-white/5 text-white/65 border border-white/10">
                      {(rec.category || "issue").replace(/_/g, " ")}
                    </span>
                    {rec.table_name && (
                      <span className="text-white/55">
                        <span className="text-white/35">table:</span>{" "}
                        <span className="font-mono text-white/80">
                          {rec.table_name}
                        </span>
                      </span>
                    )}
                    {rec.column_name && rec.column_name !== "*" && (
                      <span className="text-white/55">
                        <span className="text-white/35">column:</span>{" "}
                        <span className="font-mono text-white/80">
                          {rec.column_name}
                        </span>
                      </span>
                    )}
                    {rec.column_name === "*" && (
                      <span className="text-white/40 italic">table-level</span>
                    )}
                  </div>
                  {rec.reason && (
                    <p className="mt-1 text-[11px] text-white/45 leading-snug">
                      Reason: {rec.reason}
                    </p>
                  )}
                  <p className="mt-0.5 text-[11px] text-white/35 font-mono">
                    {rec.signature}
                  </p>
                </div>
                <button
                  onClick={() => handleRestore(rec)}
                  className="shrink-0 inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border border-white/15 text-white/70 hover:bg-white/5 hover:text-white"
                  title="Un-ignore this finding so it appears on the next run"
                >
                  <RotateCcw className="w-3 h-3" />
                  Restore
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {exportErr && (
        <div className="rounded-md border border-red-400/30 bg-red-400/10 px-3 py-2 text-xs text-red-200">
          {exportErr}
        </div>
      )}

      {/* Findings list */}
      {filtered.length === 0 ? (
        <div className="glass-surface rounded-xl p-8 text-center text-sm text-white/60">
          No findings match the current filters.
        </div>
      ) : (
        <ul className="divide-y divide-white/5 glass-surface rounded-xl overflow-hidden">
          {filtered.map((i, idx) => {
            const fp = fingerprint(i);
            return (
              <FindingRow
                key={`${i.id}-${idx}`}
                finding={i}
                selected={selected.has(fp)}
                onToggleSelect={() =>
                  setSelected((prev) => {
                    const next = new Set(prev);
                    if (next.has(fp)) next.delete(fp);
                    else next.add(fp);
                    return next;
                  })
                }
                onPromote={
                  onPromoteToRule &&
                  (i.category || "").toLowerCase() !== "business_rule"
                    ? onPromoteToRule
                    : undefined
                }
                onIgnore={projectId ? handleIgnore : undefined}
                violation={
                  i.violation_id ? violations.get(i.violation_id) : undefined
                }
                onQuery={
                  sessionId
                    ? (v) => downloadViolationQuery(sessionId, v)
                    : undefined
                }
                onExtract={
                  sessionId && canExtract
                    ? (v) => downloadViolationExtract(sessionId, v)
                    : undefined
                }
              />
            );
          })}
        </ul>
      )}
    </div>
  );
}

/**
 * Generate a concrete, actionable fix for a finding based on its
 * category, column, table, and description. Replaces generic boilerplate
 * with targeted guidance the user can act on immediately.
 */
function generateTailoredFix(finding: FindingIssue): string {
  const cat = (finding.category || "").toLowerCase();
  const col = finding.column || (finding.affected_columns?.[0] ?? "");
  const table = finding.table || "";
  const desc = (finding.description || "").toLowerCase();

  const pctMatch = finding.description?.match(/(\d+(?:\.\d+)?)\s*%/);
  const pct = pctMatch ? pctMatch[1] : null;

  if (
    cat === "completeness" ||
    desc.includes("null") ||
    desc.includes("missing")
  ) {
    if (pct) {
      return `${pct}% of "${col}" values are NULL. Backfill with a sensible default (e.g. UPDATE ${table} SET ${col} = <default> WHERE ${col} IS NULL), then add a NOT NULL constraint. Trace the upstream ETL step that produces these NULLs.`;
    }
    return `"${col}" has missing values in ${table}. Backfill NULLs and enforce NOT NULL at the DB level. Audit the ingestion pipeline for rows that skip this field.`;
  }

  if (
    cat === "uniqueness" ||
    desc.includes("duplicate") ||
    desc.includes("unique")
  ) {
    return `"${col}" has duplicate values where uniqueness is required. Identify dupes: SELECT ${col}, COUNT(*) FROM ${table} GROUP BY ${col} HAVING COUNT(*) > 1. Deduplicate rows, then add a UNIQUE constraint to prevent recurrence.`;
  }

  if (
    cat === "validity" ||
    desc.includes("format") ||
    desc.includes("pattern") ||
    desc.includes("invalid") ||
    desc.includes("type")
  ) {
    return `"${col}" in ${table} contains values that fail format or type validation. Review the sample rows below to identify the malformed pattern. Add a CHECK constraint or ingestion-time regex rule to reject bad values at source.`;
  }

  if (
    cat === "consistency" ||
    desc.includes("referential") ||
    desc.includes("foreign") ||
    desc.includes("mismatch") ||
    desc.includes("inconsistent")
  ) {
    return `"${col}" has a referential integrity or consistency violation in ${table}. Find orphaned rows: SELECT * FROM ${table} WHERE ${col} NOT IN (SELECT id FROM <parent>). Enforce FK constraints at the database level to prevent future orphans.`;
  }

  if (
    desc.includes("outlier") ||
    desc.includes("range") ||
    desc.includes("out-of-range") ||
    desc.includes("anomal")
  ) {
    return `"${col}" has out-of-range or anomalous values. Investigate: SELECT MIN(${col}), MAX(${col}), AVG(${col}), STDDEV(${col}) FROM ${table}. Add a CHECK constraint to define the valid value bounds.`;
  }

  if (
    desc.includes("cardinality") ||
    desc.includes("distinct") ||
    desc.includes("singleton")
  ) {
    return `"${col}" has unexpectedly low or high cardinality in ${table}. Verify the column's domain — this can indicate data mapping errors, a bloated enum, or a stale lookup. Review sample rows below for the pattern.`;
  }

  if (desc.includes("skew") || desc.includes("distribution")) {
    return `"${col}" has a skewed distribution that deviates from expectations. Investigate whether outlier rows represent legitimate data or ETL errors. Consider capping/flooring the value at ingestion.`;
  }

  if (
    cat === "accuracy" ||
    desc.includes("overall") ||
    desc.includes("score") ||
    desc.includes("quality score")
  ) {
    // Parse the evidence string to surface which dimensions are failing.
    // Evidence format: "completeness=0%, uniqueness=0%, consistency=100%"
    const evidence = finding.evidence || "";
    const dimMatches = evidence.match(/(\w+)=(\d+(?:\.\d+)?)%/g) ?? [];
    const failingDims = dimMatches
      .filter((m) => {
        const pctVal = parseFloat(m.split("=")[1] ?? "100");
        return pctVal < 80;
      })
      .map((m) => {
        const [dim, pctRaw] = m.split("=");
        return `${dim} (${pctRaw})`;
      });

    if (failingDims.length > 0) {
      return `Table "${table}" has a low overall quality score driven by: ${failingDims.join(", ")}. Fix each failing dimension — start with completeness (backfill NULLs, add NOT NULL constraints), then uniqueness (deduplicate rows, add UNIQUE constraints). Re-run profiling after each fix to track score improvement.`;
    }
    return `Table "${table}" has a low overall quality score. Review each column for NULL rates, duplicates, and format violations. Use the DQ tab for per-column detail and actionable fixes.`;
  }

  // Fallback — at minimum give the column + table
  return (
    finding.suggested_fix ||
    `Investigate "${col || "this issue"}" in ${table || "the table"} and remediate at source. Expand the sample rows below to identify the specific violation pattern.`
  );
}

/**
 * FindingRow — single row in the findings list with click-to-expand
 * drill-down. The offending column is highlighted in the sample table
 * (amber header + tinted cells) so users instantly see where the violation
 * lives. A tailored fix callout replaces the previous generic suggestion.
 */
function FindingRow({
  finding,
  selected,
  onToggleSelect,
  onPromote,
  onIgnore,
  violation,
  onQuery,
  onExtract,
}: {
  finding: FindingIssue;
  selected?: boolean;
  onToggleSelect?: () => void;
  onPromote?: (f: FindingIssue) => void;
  onIgnore?: (f: FindingIssue) => void;
  violation?: ViolationListing;
  onQuery?: (violationId: string) => void;
  onExtract?: (violationId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const samples = Array.isArray(finding.sample_failures)
    ? finding.sample_failures
    : [];
  const hasSamples = samples.length > 0;
  const sampleCols =
    finding.sample_failure_columns ??
    (samples[0] ? Object.keys(samples[0]) : []);

  // Build a Set of offending column names for O(1) lookup during render.
  // Special case: column === "*" means the issue is table-level (e.g. "Overall
  // quality score low"). In that case we highlight EVERY column in the sample
  // table so the user understands all columns are implicated.
  const rawOffendingCols = [
    ...(finding.column
      ? String(finding.column)
          .split(",")
          .map((c) => c.trim())
          .filter(Boolean)
      : []),
    ...(finding.affected_columns ?? []),
  ];
  const isTableLevel = rawOffendingCols.includes("*");
  const offendingCols = new Set<string>(rawOffendingCols);

  const tailoredFixText = generateTailoredFix(finding);

  // EVERY finding is expandable — even without sample rows we still have
  // an actionable fix, impact, evidence, and category context worth showing.
  // Business-rule violations in particular carry rule detail + failing-row
  // counts but no individual sample rows from the backend.
  return (
    <li className="p-0">
      <div className="flex items-start gap-3 p-3.5">
        {onToggleSelect && (
          <input
            type="checkbox"
            checked={!!selected}
            onChange={onToggleSelect}
            aria-label="Select finding for export"
            className="shrink-0 mt-1 cursor-pointer"
          />
        )}
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          aria-label={open ? "Hide details" : "Show details"}
          className="shrink-0 mt-0.5 w-4 h-4 flex items-center justify-center text-white/55 hover:text-white/90"
        >
          {open ? "▾" : "▸"}
        </button>
        <span
          className={`shrink-0 text-[11px] uppercase tracking-wider px-1.5 py-0.5 rounded ${severityPill(finding.severity)}`}
        >
          {finding.severity || "info"}
        </span>
        <span
          className={`shrink-0 text-[11px] uppercase tracking-wider px-1.5 py-0.5 rounded ${
            (finding.category || "").toLowerCase() === "business_rule"
              ? "bg-[var(--primary)]/15 text-[var(--primary)] border border-[var(--primary)]/30"
              : "bg-white/5 text-white/65 border border-white/10"
          }`}
        >
          {(finding.category || "issue").replace(/_/g, " ")}
        </span>
        <div className="flex-1 min-w-0">
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            className="text-left w-full cursor-pointer"
          >
            <p className="text-sm text-white/90 leading-snug">
              {finding.description}
            </p>
            <div className="mt-1 flex flex-wrap gap-2 text-[11px] text-white/50">
              {finding.table && (
                <span>
                  <span className="text-white/35">table:</span>{" "}
                  <span className="font-mono">{finding.table}</span>
                </span>
              )}
              {finding.column && (
                <span>
                  <span className="text-white/35">column:</span>{" "}
                  <span className="font-mono">
                    {typeof finding.column === "string"
                      ? finding.column
                      : JSON.stringify(finding.column)}
                  </span>
                </span>
              )}
              {finding.evidence && <span>{finding.evidence}</span>}
              {finding.pack_name && (
                <span
                  className="text-white/55"
                  title="Knowledge pack the rule that produced this finding ships in"
                >
                  <span className="text-white/35">pack:</span>{" "}
                  {finding.pack_name}
                  {finding.pack_version ? ` v${finding.pack_version}` : ""}
                </span>
              )}
              {finding.rule_derived_from && (
                <span className="text-white/55">customized pack rule</span>
              )}
              {finding.rule_approval_kind === "override" && (
                <span className="text-amber-300">approved by override</span>
              )}
              {hasSamples && (
                <span className="text-white/55">
                  · {samples.length} sample{samples.length === 1 ? "" : "s"}
                </span>
              )}
            </div>
            {/* Tailored fix — always shown inline (collapsed view) */}
            <div className="mt-1.5 flex items-start gap-1.5">
              <Wrench className="w-3 h-3 text-emerald-400/80 shrink-0 mt-0.5" />
              <p className="text-[11px] text-emerald-300/80 leading-snug">
                {tailoredFixText}
              </p>
            </div>
          </button>
        </div>
        <div className="shrink-0 flex flex-col items-stretch gap-1">
          {onPromote && (
            <button
              onClick={() => onPromote(finding)}
              className="text-[11px] px-2 py-1 rounded-md border border-[var(--primary)]/40 text-[var(--primary)] hover:bg-[var(--primary)]/10"
              title="Draft a reusable rule from this statistical finding"
            >
              Promote to rule
            </button>
          )}
          {violation?.has_query && onQuery && (
            <button
              onClick={() => onQuery(violation.violation_id)}
              className="inline-flex items-center justify-center gap-1 text-[11px] px-2 py-1 rounded-md border border-white/10 text-white/70 hover:bg-white/5 hover:text-white/90"
              title={
                "Download the read-only SQL that returns this finding's failing rows. Run it with your own database account; the data never leaves your environment." +
                (violation.count_note ? ` ${violation.count_note}` : "")
              }
            >
              <FileCode className="w-3 h-3" />
              Query
            </button>
          )}
          {violation?.has_query && onExtract && (
            <button
              onClick={() => onExtract(violation.violation_id)}
              className="inline-flex items-center justify-center gap-1 text-[11px] px-2 py-1 rounded-md border border-amber-400/30 text-amber-200/90 hover:bg-amber-400/10"
              title="Download this finding's failing rows as a CSV, streamed from the source: capped, audited, personal data masked. The file is client data on your machine."
            >
              <FileSpreadsheet className="w-3 h-3" />
              Extract rows
            </button>
          )}
          {violation && !violation.has_query && violation.unavailable && (
            <span
              className="text-[10px] text-white/40 max-w-[9rem] leading-tight"
              title={violation.unavailable}
            >
              No query: {violation.unavailable}
            </span>
          )}
          {onIgnore && (
            <button
              onClick={() => onIgnore(finding)}
              className="text-[11px] px-2 py-1 rounded-md border border-white/10 text-white/55 hover:bg-white/5 hover:text-white/80"
              title="Suppress this finding for this dataset on subsequent runs"
            >
              Ignore
            </button>
          )}
        </div>
      </div>

      {open && (
        <div className="mx-3.5 mb-3 space-y-2">
          {/* Expanded fix callout — always shown */}
          <div className="flex items-start gap-2 rounded-md bg-amber-400/8 border border-amber-400/20 px-3 py-2">
            <Wrench className="w-3.5 h-3.5 text-amber-300 shrink-0 mt-0.5" />
            <p className="text-[11px] text-amber-100/85 leading-snug">
              {tailoredFixText}
            </p>
          </div>

          {/* Impact + evidence rail — always useful even without samples,
              critical for business-rule findings that carry failing-row
              counts but no sample rows. */}
          {(finding.impact || finding.evidence) && (
            <div className="rounded-md bg-black/30 border border-white/10 px-3 py-2 space-y-1 text-[11px]">
              {finding.impact && (
                <div>
                  <span className="text-white/40">Impact:</span>{" "}
                  <span className="text-white/80">{finding.impact}</span>
                </div>
              )}
              {finding.evidence && (
                <div>
                  <span className="text-white/40">Evidence:</span>{" "}
                  <span className="font-mono text-white/80">
                    {finding.evidence}
                  </span>
                </div>
              )}
            </div>
          )}

          {!hasSamples && (
            <p className="text-[11px] text-white/40 italic px-1">
              No sample rows were captured — this typically happens for
              business-rule violations that report aggregate counts rather than
              individual rows.
            </p>
          )}

          {/* Sample rows — offending column highlighted */}
          {hasSamples && (
            <div
              className="rounded-md bg-black/40 border border-white/10 overflow-auto max-h-72"
              role="region"
              aria-label="Sample violating rows"
            >
              <table className="w-full text-[11px]">
                <thead className="sticky top-0 bg-black/60 backdrop-blur">
                  <tr>
                    {sampleCols.map((c) => {
                      const isOffHdr = isTableLevel || offendingCols.has(c);
                      return (
                        <th
                          key={c}
                          className={`text-left px-2 py-1 font-mono font-normal border-b border-white/10 ${
                            isOffHdr
                              ? "text-amber-300 bg-amber-400/10"
                              : "text-white/60"
                          }`}
                        >
                          {isOffHdr ? `⚠ ${c}` : c}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {samples.map((row, r) => (
                    <tr
                      key={r}
                      className="border-b border-white/5 last:border-0 hover:bg-white/[0.02]"
                    >
                      {sampleCols.map((c) => {
                        const raw = row[c];
                        const val =
                          raw === null || raw === undefined
                            ? "—"
                            : typeof raw === "boolean"
                              ? raw
                                ? "true"
                                : "false"
                              : String(raw);
                        const isOffending =
                          isTableLevel || offendingCols.has(c);
                        const isEmpty = val === "—";
                        return (
                          <td
                            key={c}
                            className={`px-2 py-1 font-mono whitespace-nowrap ${
                              isOffending
                                ? isEmpty
                                  ? "text-red-300 bg-red-400/10 font-semibold"
                                  : "text-amber-200 bg-amber-400/8"
                                : "text-white/80"
                            }`}
                          >
                            {val}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </li>
  );
}

function SummaryCard({
  icon,
  label,
  value,
  tone: _tone,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  tone: "amber" | "red" | "sky" | "violet";
}) {
  return (
    <div className="glass-surface rounded-xl p-4 flex items-center gap-3">
      <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center">
        {icon}
      </div>
      <div className="min-w-0">
        <div className="text-xs text-white/55 truncate">{label}</div>
        <div className="text-xl font-semibold text-white/90 tabular-nums">
          {value}
        </div>
      </div>
    </div>
  );
}
